import React, { useEffect, useState } from 'react';
import { crvProductService, phProductService } from '../../services/productService';
import { AmortizationOperation, AmortizationOperationRequest } from '../../types/CapitalAmortization';
import DateInput from '../common/DateInput';
import AmountInput from './AmountInput';

interface OperacionFormProps {
  operacion?: AmortizationOperation | null;
  saving: boolean;
  onSubmit: (data: AmortizationOperationRequest) => void;
  onCancel: () => void;
}

type ObjetivoTipo = 'porcentaje' | 'capital';

interface ProductOption {
  id: string;
  label: string;
}

const OperacionForm: React.FC<OperacionFormProps> = ({ operacion, saving, onSubmit, onCancel }) => {
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [productId, setProductId] = useState(operacion?.productId ?? '');
  const [descripcion, setDescripcion] = useState(operacion?.descripcion ?? '');
  const [fechaCorte, setFechaCorte] = useState(operacion?.fechaCorte ?? '');
  const [fechaDevolucionRef, setFechaDevolucionRef] = useState(operacion?.fechaDevolucionRef ?? '');
  const [objetivoTipo, setObjetivoTipo] = useState<ObjetivoTipo>(
    operacion?.capitalObjetivo != null && operacion?.porcentajeObjetivo == null ? 'capital' : 'porcentaje');
  const [porcentaje, setPorcentaje] = useState(
    operacion?.porcentajeObjetivo != null ? String(+(operacion.porcentajeObjetivo * 100).toFixed(4)).replace('.', ',') : '');
  const [capitalObjetivo, setCapitalObjetivo] = useState<number | undefined>(operacion?.capitalObjetivo ?? undefined);
  const [redondeo, setRedondeo] = useState<number | undefined>(operacion?.redondeo ?? 10000);
  const [destinoReinversion, setDestinoReinversion] = useState(operacion?.destinoReinversion ?? '');
  const [notas, setNotas] = useState(operacion?.notas ?? '');

  useEffect(() => {
    Promise.all([crvProductService.getAll(), phProductService.getAll()])
      .then(([crv, ph]) => {
        const options = [
          ...crv.data.map(p => ({ id: p.id!, label: `${p.id} · ${p.nombreProducto} (CRV)` })),
          ...ph.data.map(p => ({ id: p.id!, label: `${p.id} · ${p.nombreProducto} (PH)` })),
        ];
        setProducts(options);
      })
      .catch(() => setProducts([]));
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const pct = parseFloat(porcentaje.replace(',', '.'));
    onSubmit({
      productId,
      descripcion: descripcion.trim(),
      fechaCorte,
      fechaDevolucionRef: fechaDevolucionRef || null,
      porcentajeObjetivo: objetivoTipo === 'porcentaje' && !Number.isNaN(pct) ? pct / 100 : null,
      capitalObjetivo: objetivoTipo === 'capital' ? capitalObjetivo ?? null : null,
      redondeo: redondeo ?? null,
      destinoReinversion: destinoReinversion.trim() || null,
      notas: notas.trim() || null,
    });
  };

  return (
    <form className="amz-form" onSubmit={handleSubmit}>
      <div className="amz-form-grid">
        <div className="amz-field amz-field-wide">
          <label htmlFor="amz-producto">Producto *</label>
          <select id="amz-producto" value={productId} onChange={e => setProductId(e.target.value)} required
                  disabled={!!operacion && operacion.numInversores > 0}>
            <option value="">Selecciona un producto CRV o PH</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        <div className="amz-field amz-field-wide">
          <label htmlFor="amz-descripcion">Descripción *</label>
          <input id="amz-descripcion" type="text" value={descripcion} required maxLength={255}
                 onChange={e => setDescripcion(e.target.value)} />
        </div>
        <div className="amz-field">
          <label htmlFor="amz-corte">Fecha de corte *</label>
          <DateInput id="amz-corte" value={fechaCorte} required onChange={e => setFechaCorte(e.target.value)} />
        </div>
        <div className="amz-field">
          <label htmlFor="amz-ref">Fecha devolución de referencia</label>
          <DateInput id="amz-ref" value={fechaDevolucionRef} onChange={e => setFechaDevolucionRef(e.target.value)} />
          <span className="amz-hint">Vacía: la estimada inicial de cada fase</span>
        </div>
        <div className="amz-field">
          <label>Objetivo</label>
          <div className="amz-radio-group">
            <label><input type="radio" checked={objetivoTipo === 'porcentaje'} onChange={() => setObjetivoTipo('porcentaje')} /> % por ficha</label>
            <label><input type="radio" checked={objetivoTipo === 'capital'} onChange={() => setObjetivoTipo('capital')} /> Capital total</label>
          </div>
        </div>
        <div className="amz-field">
          {objetivoTipo === 'porcentaje' ? (
            <>
              <label htmlFor="amz-pct">% a amortizar</label>
              <input id="amz-pct" type="text" inputMode="decimal" value={porcentaje} placeholder="Ej.: 53,26"
                     onChange={e => setPorcentaje(e.target.value)} />
            </>
          ) : (
            <>
              <label htmlFor="amz-capital">Capital a amortizar (€)</label>
              <AmountInput id="amz-capital" value={capitalObjetivo} onChange={setCapitalObjetivo} placeholder="Ej.: 490.000,00" />
            </>
          )}
        </div>
        <div className="amz-field">
          <label htmlFor="amz-redondeo">Redondeo de la propuesta (€)</label>
          <AmountInput id="amz-redondeo" value={redondeo} onChange={setRedondeo} />
          <span className="amz-hint">Hacia abajo, al múltiplo indicado</span>
        </div>
        <div className="amz-field">
          <label htmlFor="amz-destino">Proyecto de reinversión</label>
          <input id="amz-destino" type="text" value={destinoReinversion} maxLength={200}
                 onChange={e => setDestinoReinversion(e.target.value)} />
        </div>
        <div className="amz-field amz-field-full">
          <label htmlFor="amz-notas">Notas</label>
          <textarea id="amz-notas" rows={2} value={notas} onChange={e => setNotas(e.target.value)} />
        </div>
      </div>
      <div className="amz-form-actions">
        <button type="button" className="amz-btn amz-btn-secondary" onClick={onCancel} disabled={saving}>Cancelar</button>
        <button type="submit" className="amz-btn amz-btn-primary" disabled={saving}>
          {saving ? 'Guardando…' : operacion ? 'Guardar cambios' : 'Crear operación'}
        </button>
      </div>
    </form>
  );
};

export default OperacionForm;
